import { type OrganizationRole } from '@funnellab/shared/src/types/directus-enums.ts';
import { Paths } from '@src/paths';
import { RecruiterView } from '@src/components/jobDetails/recruiter/RecruiterView';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecruiter } from '@src/hooks/jobwizzard/useRecruiter';
import { useRecruiterRights } from '@src/hooks/recruiter/useRecruiterRights';
import { useUserContext } from '@src/contexts/useUserContext.ts';
import recruiterService, {
  type AcceptedRecruiter,
  type Recruiter,
} from '@src/api/services/RecruiterService.ts';

export default function Recruiter({
  organizationId,
}: {
  readonly organizationId: string;
}) {
  const { user } = useUserContext();
  const navigate = useNavigate();

  const { data: recruiter, refresh } = useRecruiter(organizationId);

  const nonNullableRecruiters = useMemo(() => recruiter ?? [], [recruiter]);

  const { canUpdateRole } = useRecruiterRights({
    allRecruiters: nonNullableRecruiters,
  });

  const removeRecruiter = useCallback(
    async (idToRemove: string) => {
      const recruiterToRemove = nonNullableRecruiters.find(
        (r) => r.id === idToRemove,
      );
      if (!recruiterToRemove) {
        return;
      }
      if (recruiterToRemove.accepted) {
        const isMe = idToRemove === user.id;
        try {
          await recruiterService.removeRecruiter(organizationId, idToRemove);
        } finally {
          if (isMe) {
            navigate(Paths.ORGANIZATIONS);
          } else {
            refresh();
          }
        }
      } else {
        try {
          await recruiterService.removeInvitedRecruiter(
            organizationId,
            idToRemove,
          );
        } finally {
          refresh();
        }
      }
    },
    [nonNullableRecruiters, user.id, organizationId, navigate, refresh],
  );

  const onRoleChange = useCallback(
    async (recruiter: AcceptedRecruiter, role: `${OrganizationRole}`) => {
      if (role === recruiter.role) {
        return;
      }
      if (!canUpdateRole(recruiter)) {
        throw new Error('Role cannot be updated');
      }
      await recruiterService.updateRecruiterRole(
        organizationId,
        recruiter.id,
        role,
      );
      refresh();
    },
    [canUpdateRole, organizationId, refresh],
  );

  return (
    <RecruiterView
      className="h-full"
      recruiter={nonNullableRecruiters}
      allRecruiters={nonNullableRecruiters}
      organizationId={organizationId}
      onRoleChange={onRoleChange}
      onDelete={removeRecruiter}
      onRecruiterAdded={refresh}
    />
  );
}
